import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { BetaStatusContent, Tip, ReportingMethod, Stat, DEFAULT_CONTENT } from './beta/betaTypes';
import BetaListEditors from './beta/BetaListEditors';

export const BetaStatusEditor: React.FC = () => {
  const [content, setContent] = useState<BetaStatusContent>(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editingTip, setEditingTip] = useState<number | null>(null);
  const [editingReport, setEditingReport] = useState<number | null>(null);
  const [editingStat, setEditingStat] = useState<number | null>(null);

  const showNotif = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_site_content', { p_key: 'beta_status' });
      if (error) throw error;
      if (data && Object.keys(data).length > 0) {
        setContent({ ...DEFAULT_CONTENT, ...data });
      }
    } catch (err: any) {
      showNotif(`Failed to load: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_set_site_content', {
        p_key: 'beta_status',
        p_value: content,
      });
      if (error) throw error;
      showNotif('Beta status content saved', 'success');
    } catch (err: any) {
      showNotif(`Failed to save: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const addTip = () => {
    setContent(prev => ({
      ...prev,
      tips: [...prev.tips, { title: '', description: '' }],
    }));
    setEditingTip(content.tips.length);
  };

  const updateTip = (index: number, field: keyof Tip, value: string) => {
    setContent(prev => ({
      ...prev,
      tips: prev.tips.map((t, i) => i === index ? { ...t, [field]: value } : t),
    }));
  };

  const removeTip = (index: number) => {
    setContent(prev => ({ ...prev, tips: prev.tips.filter((_, i) => i !== index) }));
    setEditingTip(null);
  };

  const addReporting = () => {
    setContent(prev => ({
      ...prev,
      reporting: [...prev.reporting, { title: '', icon: '📋', items: [] }],
    }));
    setEditingReport(content.reporting.length);
  };

  const updateReporting = (index: number, updates: Partial<ReportingMethod>) => {
    setContent(prev => ({
      ...prev,
      reporting: prev.reporting.map((r, i) => i === index ? { ...r, ...updates } : r),
    }));
  };

  const removeReporting = (index: number) => {
    setContent(prev => ({ ...prev, reporting: prev.reporting.filter((_, i) => i !== index) }));
    setEditingReport(null);
  };

  const addStat = () => {
    setContent(prev => ({
      ...prev,
      stats: [...prev.stats, { label: '', value: '' }],
    }));
    setEditingStat(content.stats.length);
  };

  const updateStat = (index: number, updates: Partial<Stat>) => {
    setContent(prev => ({
      ...prev,
      stats: prev.stats.map((s, i) => i === index ? { ...s, ...updates } : s),
    }));
  };

  const removeStat = (index: number) => {
    setContent(prev => ({ ...prev, stats: prev.stats.filter((_, i) => i !== index) }));
    setEditingStat(null);
  };

  if (loading) {
    return (
      <div className="rp-bento-card mb-6">
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4" />
          <p style={{ color: 'var(--rp-text-secondary)' }}>Loading beta status...</p>
        </div>
      </div>
    );
  }

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'var(--rp-text)',
  };

  return (
    <div className="rp-bento-card mb-6">
      {notification && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm font-semibold shadow-lg flex items-center gap-2"
          style={{
            backgroundColor: notification.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            color: notification.type === 'success' ? '#10b981' : '#ef4444',
            border: `1px solid ${notification.type === 'success' ? '#10b981' : '#ef4444'}`,
          }}
        >
          {notification.message}
          <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-70 admin-icon-btn" aria-label="Dismiss notification">×</button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>Beta Status Content</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
            Edit the Beta Testing Guide shown on the Status Page
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/status" target="_blank" rel="noopener noreferrer"
            className="px-3 py-2 rounded-lg text-sm transition-all hover:bg-white/5"
            style={{ color: 'var(--rp-text-secondary)', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            View Status Page
          </a>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--admin-primary)' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Visibility Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
        <div>
          <span className="font-semibold text-sm" style={{ color: 'var(--rp-text)' }}>Section Visibility</span>
          <p className="text-xs mt-0.5" style={{ color: 'var(--rp-text-secondary)' }}>
            {content.visible ? 'Visible on Status Page' : 'Hidden from Status Page'}
          </p>
        </div>
        <button
          onClick={() => setContent(prev => ({ ...prev, visible: !prev.visible }))}
          className="admin-pubtoggle"
          aria-label="Toggle beta section visibility"
        >
          <span
            className="admin-pubtoggle-track"
            style={{ backgroundColor: content.visible ? '#10b981' : '#6b7280' }}
          >
            <span
              className="admin-pubtoggle-knob"
              style={{ transform: content.visible ? 'translateX(28px)' : 'translateX(0)' }}
            />
          </span>
        </button>
      </div>

      {/* Title & Subtitle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="beta-section-title" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Section Title</label>
          <input id="beta-section-title" type="text" value={content.title}
            onChange={e => setContent(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="beta-section-subtitle" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Subtitle</label>
          <input id="beta-section-subtitle" type="text" value={content.subtitle}
            onChange={e => setContent(prev => ({ ...prev, subtitle: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
          />
        </div>
      </div>

      {/* Section Subheaders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="beta-tips-header" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Tips Section Header</label>
          <input id="beta-tips-header" type="text" value={content.tips_header || ''}
            onChange={e => setContent(prev => ({ ...prev, tips_header: e.target.value }))}
            placeholder="Testing Best Practices"
            className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="beta-reporting-header" className="text-xs font-semibold mb-1 block" style={{ color: 'var(--rp-text-secondary)' }}>Reporting Section Header</label>
          <input id="beta-reporting-header" type="text" value={content.reporting_header || ''}
            onChange={e => setContent(prev => ({ ...prev, reporting_header: e.target.value }))}
            placeholder="How to Report Issues"
            className="w-full px-3 py-2 rounded-lg text-sm" style={inputStyle}
          />
        </div>
      </div>

      <BetaListEditors
        content={content}
        editingTip={editingTip}
        editingReport={editingReport}
        editingStat={editingStat}
        setEditingTip={setEditingTip}
        setEditingReport={setEditingReport}
        setEditingStat={setEditingStat}
        addTip={addTip}
        updateTip={updateTip}
        removeTip={removeTip}
        addReporting={addReporting}
        updateReporting={updateReporting}
        removeReporting={removeReporting}
        addStat={addStat}
        updateStat={updateStat}
        removeStat={removeStat}
      />
    </div>
  );
};
